import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ResponsiveContainer, LineChart, XAxis, YAxis, Line, CartesianGrid, Tooltip } from 'recharts';

const colors = {
  line0:  '#1E88E5',
  line1:  '#FB8C00',
  line2:  '#00C853',
  line3:  '#C15EAF',
  line4:  '#00B8D9',
  line5:  '#DF4949',
  line6:  '#FFB300',
  line7:  '#6C5CE7',
  line8:  '#00897B',
  line9:  '#F4511E',
  line10: '#5CB91A',
  line11: '#8E24AA',
  line12: '#29B6F6',
  line13: '#EF5350',
  line14: '#9CCC65',
  line15: '#5E35B1',
  line16: '#FFA726',
  line17: '#2FB2AD',
  line18: '#E91E63',
  line19: '#66BB6A',
  line20: '#3949AB',
  line21: '#FF7043',
  line22: '#AB47BC',
  line23: '#F7CC00',
  line24: '#D81B60',
  line25: '#82C6D7',
  line26: '#6081EE',
  line27: '#BA9762',
  line28: '#EC407A',
  line29: '#7E57C2',
  line30: '#9B603E',
  line31: '#F2BDDE',
  line32: '#B0CB1D',
};

const CustomAxisTick = (props) => {
  const { x, y, payload, dateRange } = props;

  const dateDifferenceInDays = moment(dateRange.endTime).diff(moment(dateRange.startTime), 'days');
  let dateFormat = '';

  if (dateDifferenceInDays < 2) {
    dateFormat = 'MMM D (h:mm a)';
  } else if (dateDifferenceInDays > 2 && dateDifferenceInDays < 30) {
    dateFormat = 'MMM D (h:mm a)';
  } else {
    dateFormat = 'MMM D (h:mm a)';
  }

  const formattedDate = moment(payload.value).format(dateFormat);

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={15} dy={10} dx={-50} fill="#475867">
        {formattedDate}
      </text>
    </g>
  );
};

function restructureData(formattedDataPoints) {
  const keys = Object.keys(formattedDataPoints);
  const dataArray = [];
  let longest = 0;
  let longestData = [];

  // create initial object with all keys set to null
  const initializeDataPoints = keys.reduce((o, key) => ({ ...o, [key]: null }), {});

  // find the data point with the most data points
  keys.forEach((reg) => {
    if (formattedDataPoints[reg].length > longest) {
      longestData = formattedDataPoints[reg];
      longest = formattedDataPoints[reg].length;
    }
  });

  // add initialized registers with null data, longest data set and dates to dataArray
  longestData.forEach((dataset) => {
    dataArray.push({
      ...initializeDataPoints,
      ...dataset,
    });
  });

  // go through the registers and replace null values if applicable
  keys.forEach((register) => {
    const values = formattedDataPoints[register];
    values.forEach((dataset, index) => {
      if (formattedDataPoints[register].length === longest) {
        // If the data length is the same as the longest data length
        // ie. there is no missing data
        dataArray[index][register] = dataset[register];
      } else {
        const indexOfDate = dataArray.findIndex((element) => {
          const { _i: elDateFormat } = element.date;
          const { _i: datasetDateFormat } = dataset.date;
          return elDateFormat === datasetDateFormat;
        });
        dataArray[indexOfDate][register] = dataset[register];
      }
    });
  });
  return dataArray;
}

export const MonitoringLineChart = (props) => {
  const { data, dateRange, selectedCategory, theme, colorObj } = props;
  const [formattedData, setFormmattedData] = useState({});
  const [dataPoints, setDataPoints] = useState([]);
  const [dataPointColors, setDataPointColors] = useState([]);

  const assignColors = (formattedTags) => {
    const selectedColors = {};
    formattedTags.forEach((dataPoint) => {
      selectedColors[dataPoint] = colors[colorObj[dataPoint]];
    });
    setDataPointColors(selectedColors);
  };

  useEffect(() => {
    const formattedDataPoints = {};
    const formattedTags = [];

    if (data.data_points) {
      data.data_points.map((dataSet) => {
        return formattedTags.push(dataSet.id);
      });

      data.data_points.map((dataSet) => {
        formattedDataPoints[dataSet.id] = dataSet.graph_data.map((d) => ({
          date: d.x,
          [dataSet.id]: d.actual,
        }));

        return formattedDataPoints;
      });
    }

    setDataPoints(formattedTags);
    setFormmattedData(restructureData(formattedDataPoints));
    assignColors(formattedTags);
  }, [data, selectedCategory, colorObj]);

  const formatTime = () => {
    const dateDifferenceInDays = moment(dateRange.endTime).diff(moment(dateRange.startTime), 'days');
    let dateFormat = '';

    if (dateDifferenceInDays < 2) {
      dateFormat = 'MMM D, YYYY (LT)';
    } else if (dateDifferenceInDays > 2 && dateDifferenceInDays < 30) {
      dateFormat = 'MMM D, YYYY (LT)';
    } else {
      dateFormat = 'MMM D, YYYY (LT)';
    }

    return dateFormat;
  };

  const tooltipFormatter = (value, name) => {
    let metric = '';
    let displayName = '';

    if (data.data_points) {
      data.data_points.forEach((datapoint) => {
        if (datapoint.id === name) {
          metric = datapoint.unit;
          displayName = datapoint.name;
        }
      });
    }

    return [`${value.toFixed(2)} ${metric}`, displayName];
  };

  const getTooltipStyle = () => {
    const style = theme === 'light' ? { background: '#ffffff' } : { background: '#141414' };
    return style;
  };

  return (
    <ResponsiveContainer>
      <LineChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis type="category" height={80} dataKey="date" tick={<CustomAxisTick dateRange={dateRange} />} />
        <YAxis type="number" />
        {dataPoints.map((dataPoint) => {
          return (
            <Line dataKey={dataPoint} stroke={dataPointColors[dataPoint]} dot={false} strokeWidth={2} key={dataPoint} />
          );
        })}
        <Tooltip
          contentStyle={getTooltipStyle()}
          isAnimationActive={false}
          labelFormatter={(label) => moment(label).format(formatTime())}
          formatter={(value, name) => tooltipFormatter(value, name)}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

MonitoringLineChart.propTypes = {
  data: PropTypes.shape({
    date: PropTypes.string,
    interval: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        graph_data: PropTypes.arrayOf(
          PropTypes.shape({
            actual: PropTypes.number,
            x: PropTypes.string,
          }),
        ),
        id: PropTypes.string,
        name: PropTypes.string,
        tag: PropTypes.string,
        unit: PropTypes.string,
      }),
    ),
  }).isRequired,
};
